import axios from 'axios';
import { AuthResponse, EntryExitConfig, DashboardStats, Portfolio } from '../types';
import { API_CONFIG, ENDPOINTS } from './config';

const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Skip redirect for auth routes — let them handle errors themselves
    const isAuthRoute = error.config?.url?.includes('/auth/');
    if (error.response?.status === 401 && !isAuthRoute) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username: string, password: string): Promise<AuthResponse> => {
    const response = await api.post(ENDPOINTS.AUTH.LOGIN, { username, password });
    return response.data;
  },

  register: async (username: string, email: string, password: string, dhan_client_id: string, dhan_access_token: string): Promise<AuthResponse> => {
    const response = await api.post(ENDPOINTS.AUTH.REGISTER, { username, email, password, dhan_client_id, dhan_access_token });
    return response.data;
  },
};

export const configAPI = {
  getConfigs: async (): Promise<EntryExitConfig[]> => {
    const response = await api.get(ENDPOINTS.CONFIG.GET);
    return response.data;
  },

  createConfig: async (config: Omit<EntryExitConfig, 'id'>): Promise<EntryExitConfig> => {
    const response = await api.post(ENDPOINTS.CONFIG.CREATE, config);
    return response.data;
  },

  updateConfig: async (id: number, config: Partial<EntryExitConfig>): Promise<EntryExitConfig> => {
    const response = await api.put(`${ENDPOINTS.CONFIG.UPDATE}/${id}`, config);
    return response.data;
  },

  duplicateConfig: async (id: number): Promise<EntryExitConfig> => {
    const response = await api.post(`${ENDPOINTS.CONFIG.CREATE}/${id}/duplicate`);
    return response.data;
  },

  toggleFilter: async (id: number, filterEnabled: boolean): Promise<EntryExitConfig> => {
    const response = await api.put(`${ENDPOINTS.CONFIG.UPDATE}/${id}`, { filter_enabled: filterEnabled });
    return response.data;
  },

  deleteConfig: async (id: number): Promise<void> => {
    await api.delete(`${ENDPOINTS.CONFIG.DELETE}/${id}`);
  },

  bulkEnable: async (ids: number[]): Promise<void> => {
    await api.put(`${ENDPOINTS.CONFIG.UPDATE}/bulk-enable`, { ids });
  },
};

export const bseConfigAPI = {
  getConfigs: async (): Promise<EntryExitConfig[]> => {
    const response = await api.get(ENDPOINTS.BSE_CONFIG.GET);
    return response.data;
  },

  createConfig: async (config: Omit<EntryExitConfig, 'id'>): Promise<EntryExitConfig> => {
    const response = await api.post(ENDPOINTS.BSE_CONFIG.CREATE, config);
    return response.data;
  },

  updateConfig: async (id: number, config: Partial<EntryExitConfig>): Promise<EntryExitConfig> => {
    const response = await api.put(`${ENDPOINTS.BSE_CONFIG.UPDATE}/${id}`, config);
    return response.data;
  },

  duplicateConfig: async (id: number): Promise<EntryExitConfig> => {
    const response = await api.post(`${ENDPOINTS.BSE_CONFIG.CREATE}/${id}/duplicate`);
    return response.data;
  },

  toggleFilter: async (id: number, filterEnabled: boolean): Promise<EntryExitConfig> => {
    const response = await api.put(`${ENDPOINTS.BSE_CONFIG.UPDATE}/${id}`, { filter_enabled: filterEnabled });
    return response.data;
  },

  deleteConfig: async (id: number): Promise<void> => {
    await api.delete(`${ENDPOINTS.BSE_CONFIG.DELETE}/${id}`);
  },

  bulkEnable: async (ids: number[]): Promise<void> => {
    await api.put(`${ENDPOINTS.BSE_CONFIG.UPDATE}/bulk-enable`, { ids });
  },
};

export const bseOrdersAPI = {
  getOrders: async (page = 1, limit = 50) => {
    const response = await api.get(`${ENDPOINTS.BSE_ORDERS.GET}?page=${page}&limit=${limit}`);
    return response.data;
  },

  getOpenOrders: async () => {
    const response = await api.get(ENDPOINTS.BSE_ORDERS.OPEN);
    return response.data;
  },

  exitAll: async () => {
    const response = await api.post(ENDPOINTS.BSE_ORDERS.EXIT_ALL);
    return response.data;
  },

  clearHistory: async () => {
    const response = await api.delete(ENDPOINTS.BSE_ORDERS.CLEAR);
    return response.data;
  },

  deletePermanently: async () => {
    const response = await api.delete(ENDPOINTS.BSE_ORDERS.DELETE);
    return response.data;
  },
};

export const portfolioAPI = {
  getPortfolio: async (): Promise<Portfolio[]> => {
    const response = await api.get(ENDPOINTS.PORTFOLIO.GET);
    return response.data;
  },

  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await api.get(ENDPOINTS.PORTFOLIO.DASHBOARD);
    return response.data;
  },
};

export const tradeSettingsAPI = {
  getSettings: async (exchange: 'NSE' | 'BSE' = 'NSE') => {
    const response = await api.get(`/trade-settings?exchange=${exchange}`);
    return response.data;
  },

  updateSettings: async (settings: any) => {
    const response = await api.put('/trade-settings', settings);
    return response.data;
  },
};

export const filterSettingsAPI = {
  getSettings: async () => {
    const response = await api.get('/filter-settings');
    return response.data;
  },

  updateSettings: async (settings: any) => {
    const response = await api.put('/filter-settings', settings);
    return response.data;
  },
};

export const liveDataAPI = {
  getSymbols: async () => {
    const response = await api.get('/live-data/symbols');
    return response.data;
  },

  searchSymbols: async (query: string) => {
    const response = await api.get(`/live-data/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  getLiveData: async (page = 1, limit = 10, search = '') => {
    console.log('[API] Calling getLiveData:', { page, limit, search });
    const params = new URLSearchParams({ 
      page: page.toString(), 
      limit: limit.toString() 
    });
    if (search) params.append('search', search);
    
    const response = await api.get(`/live-data/live?${params}`, { timeout: 10000 });
    console.log('[API] getLiveData response:', response.data);
    return response.data;
  },

  getBatchLiveData: async (symbols: string[]) => {
    const response = await api.post('/live-data/batch', { symbols }, { timeout: 8000 });
    return response.data;
  },

  getXYStockSymbols: async (): Promise<{ mode: number; count: number; symbols: string[] }> => {
    const response = await api.get('/live-data/xy-stock-symbols');
    return response.data;
  },
};

export const dhanAPI = {
  testPlaceOrder: async (mode: 'LIVE' | 'TEST' = 'TEST') => {
    const response = await api.post('/dhan/test-order', { mode });
    return response.data;
  },

  placeOrder: async (orderData: any) => {
    const response = await api.post('/dhan/orders', orderData);
    return response.data;
  },

  getOrders: async () => {
    const response = await api.get('/dhan/orders');
    return response.data;
  },

  getPositions: async () => {
    const response = await api.get('/dhan/positions');
    return response.data;
  },
};

export const ordersAPI = {
  getOrders: async (page = 1, limit = 50) => {
    const response = await api.get(`/orders?page=${page}&limit=${limit}`);
    return response.data;
  },

  clearHistory: async () => {
    const response = await api.delete('/orders/clear');
    return response.data;
  },

  deletePermanently: async () => {
    const response = await api.delete('/orders/delete-permanently');
    return response.data;
  },
};

export default api;